import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Search } from "lucide-react";
import Logo from "./Logo";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/data/branches";
import SmartSearch from "./SmartSearch";
import UnifiedLanguageSwitcher from "./UnifiedLanguageSwitcher";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/studio-experience", label: "Studio", highlight: true },
  { to: "/demo-booking", label: "Book Demo" },
  { to: "/free-resources", label: "Free PDFs" },
  { to: "/cities", label: "Locations" },
  { to: "/branches", label: "Centers" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

// AI Wizard moved out of the top nav (was causing overlap with the
// language selectors). Still accessible via the homepage CTAs, the
// footer "AI Product Recommendation" link, AND on mobile via the menu.
const MOBILE_EXTRA = [{ to: "/recommendation", label: "AI Wizard" }];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [pathname]);

  // Cmd/Ctrl + K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header
      className={`sticky top-0 z-40 border-b transition-all ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-slate-200"
          : "bg-white border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="si-container flex items-center justify-between h-20 md:h-24">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                n.highlight
                  ? `px-3 py-2 rounded-md text-sm font-bold transition-all inline-flex items-center gap-1.5 ${
                      isActive
                        ? "bg-[#EA580C] text-white shadow-md"
                        : "text-[#EA580C] hover:bg-orange-50 ring-1 ring-orange-200"
                    }`
                  : `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "text-slate-900 bg-[#EFF6FF]"
                        : "text-slate-700 hover:text-slate-900 hover:bg-slate-50"
                    }`
              }
            >
              {n.highlight && <i className="fa-solid fa-sparkles text-[10px]" />}
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <button
            onClick={() => setSearchOpen(true)}
            className="px-3 py-2 rounded-md text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-50 flex items-center gap-2"
            data-testid="nav-search-btn"
            aria-label="Search"
          >
            <Search size={16} />
            <kbd className="text-[10px] px-1 py-0.5 rounded bg-slate-100 text-slate-500 font-mono">⌘K</kbd>
          </button>
          <LanguageSlot />
          <a
            href={`tel:${COMPANY.sales_phone}`}
            className="text-sm font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-2"
            data-testid="nav-call-link"
          >
            <i className="fa-solid fa-phone-volume text-slate-900" />
            {COMPANY.sales_phone}
          </a>
          <Button
            asChild
            className="bg-[#EA580C] hover:bg-[#C2410C] text-white shadow"
            data-testid="nav-book-demo-btn"
          >
            <Link to="/demo-booking">Book Free Demo</Link>
          </Button>
        </div>
        <div className="lg:hidden flex items-center gap-1">
          <UnifiedLanguageSwitcher compact />
          <button
            onClick={() => setSearchOpen(true)}
            className="p-2 text-slate-700"
            data-testid="mobile-search-btn"
            aria-label="Search"
          >
            <Search size={22} />
          </button>
          <button
            onClick={() => setOpen(!open)}
            className="p-2 text-slate-700"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="si-container py-4 flex flex-col gap-2">
            {[...NAV, ...MOBILE_EXTRA].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  n.highlight
                    ? `px-3 py-2 rounded-md font-bold inline-flex items-center gap-2 ${
                        isActive
                          ? "bg-[#EA580C] text-white"
                          : "text-[#EA580C] bg-orange-50 ring-1 ring-orange-200"
                      }`
                    : `px-3 py-2 rounded-md font-medium ${
                        isActive
                          ? "text-slate-900 bg-[#EFF6FF]"
                          : "text-slate-700 hover:bg-slate-50"
                      }`
                }
              >
                {n.highlight && <i className="fa-solid fa-sparkles text-[10px]" />}
                {n.label}
              </NavLink>
            ))}
            <Button asChild className="bg-[#EA580C] hover:bg-[#C2410C] mt-2">
              <Link to="/demo-booking">Book Free Demo</Link>
            </Button>
          </div>
        </div>
      )}
      {searchOpen && <SmartSearch onClose={() => setSearchOpen(false)} />}
    </header>
  );
}

// Single source of truth for the desktop language picker.
// Wrapping in its own component lets us swap implementations later
// without touching the surrounding navbar markup.
function LanguageSlot() {
  return <UnifiedLanguageSwitcher />;
}

